import React from 'react';
import { bool, node, string } from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { selectLastPageOutsideAuth } from '../../ducks/routing.duck';
import { Icons, LinkedLogo, NamedLink } from '../../components';
import authPhoto from '../../assets/signup-bg.webp';

import css from './AuthLayout.module.css';

/**
 * The heading of an authentication screen: an optional mark above it (a back link or the
 * mail icon), the title, and a line under it.
 */
export const AuthHeading = props => {
  const { top, title, subtitle } = props;
  return (
    <div className={css.heading}>
      {top}
      <h1 className={css.title}>{title}</h1>
      {subtitle ? <p className={css.subtitle}>{subtitle}</p> : null}
    </div>
  );
};

/** A login, signup or other failure, shown above the form. */
export const AuthError = props => <div className={css.error}>{props.children}</div>;

/** A line under the form, e.g. "Don't have an account? Sign up". */
export const AuthNote = props => {
  const { className, children } = props;
  return <p className={classNames(css.note, className)}>{children}</p>;
};

/** A link back to another screen, with a left chevron, above the heading. */
export const AuthBackLink = props => {
  const { name, children } = props;
  return (
    <NamedLink name={name} className={css.backLink}>
      <svg className={css.backIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
          d="M10 3 5 8l5 5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {children}
    </NamedLink>
  );
};

/** The pale green circle with an envelope, above "Check your email" and the like. */
export const AuthMailIcon = () => (
  <span className={css.mailIcon} aria-hidden="true">
    <Icons name="accountInbox" />
  </span>
);

/**
 * The shell of every authentication page (log in, sign up, confirm, password recovery and
 * reset, email verification). From 1024px: two columns, the form on the left under the green
 * logo with the legal links at the bottom, the photo with the white logo and the tagline on the
 * right. Below 1024px: one column, a photo strip with the white logo and the tagline on top.
 * No topbar, footer or bottom nav.
 */
const AuthLayout = props => {
  const { top, title, subtitle, wide, children } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  // Close: back to the page the visitor was on before signing in (the landing page when they
  // came straight here), decided when tapped
  const lastPage = useSelector(selectLastPageOutsideAuth);
  const onClose = () => history.push(lastPage || '/');

  return (
    <div className={css.root}>
      <div className={css.main}>
        <LinkedLogo className={css.logoLink} logoClassName={css.logo} />
        <div className={css.body}>
          <div className={classNames(css.content, { [css.contentWide]: wide })}>
            {title ? <AuthHeading top={top} title={title} subtitle={subtitle} /> : null}
            {children}
          </div>
        </div>
        <nav className={css.legal} aria-label={intl.formatMessage({ id: 'AuthLayout.legalLinks' })}>
          <NamedLink name="TermsOfServicePage" className={css.legalLink}>
            <FormattedMessage id="AuthenticationPage.termsAndConditionsTermsLinkText" />
          </NamedLink>
          <NamedLink name="PrivacyPolicyPage" className={css.legalLink}>
            <FormattedMessage id="AuthenticationPage.termsAndConditionsPrivacyLinkText" />
          </NamedLink>
          <a className={css.legalLink} href={`mailto:${config.supportEmail}`}>
            <FormattedMessage id="AuthLayout.help" />
          </a>
        </nav>
      </div>
      <div className={css.photo} style={{ backgroundImage: `url(${authPhoto})` }}>
        <LinkedLogo className={css.photoLogoLink} logoClassName={css.photoLogo} />
        {/* Phones and tablets: a plain way out, back to where the visitor was */}
        <button
          type="button"
          className={css.close}
          onClick={onClose}
          aria-label={intl.formatMessage({ id: 'AuthLayout.close' })}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>
        <p className={css.tagline}>
          <FormattedMessage id="SignupForm.tagline" />
        </p>
      </div>
    </div>
  );
};

AuthLayout.defaultProps = {
  top: null,
  title: null,
  subtitle: null,
  wide: false,
  children: null,
};

AuthLayout.propTypes = {
  top: node,
  title: node,
  subtitle: node,
  // The sign-up form's wider column
  wide: bool,
  children: node,
};

AuthNote.defaultProps = { className: null, children: null };
AuthNote.propTypes = { className: string, children: node };

AuthBackLink.propTypes = { name: string.isRequired, children: node.isRequired };

export default AuthLayout;
